import { Link } from "react-router-dom";
import type { CSSProperties } from "react";
import type { WebsiteThemeId } from "@gigflow/shared";
import { formatMoney } from "../api";
import { sitePath } from "../site-context";
import { themeCssVars } from "../themes";

export type PublicSiteData = {
  business: {
    id: string;
    name: string;
    slug: string;
    industry: string;
    description?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    logoUrl?: string | null;
    heroHeadline?: string | null;
    callToAction?: string | null;
    aboutContent?: string | null;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    currencyCode?: string | null;
    businessHours?: Record<string, unknown> | null;
  };
  website: {
    themeId: WebsiteThemeId;
    layoutFamily?: string;
    status: string;
    sectionOrder: string[];
    enabledSections: string[];
    seoTitle?: string | null;
    seoDescription?: string | null;
    sections: Array<{
      key: string;
      title?: string | null;
      content: Record<string, unknown>;
      isEnabled?: boolean;
    }>;
  };
  services: Array<{
    id: string;
    name: string;
    description?: string | null;
    pricingType: string;
    priceCents?: number | null;
    durationMinutes?: number | null;
    imageUrl?: string | null;
  }>;
  serviceAreas: Array<{
    id: string;
    name: string;
    description?: string | null;
  }>;
  policies: Array<{ key: string; title: string; body: string }>;
  reviews: Array<{
    id: string;
    rating: number;
    comment?: string | null;
    client?: { user?: { fullName?: string } };
  }>;
};

function sectionMap(site: PublicSiteData) {
  const map = new Map(site.website.sections.map((s) => [s.key, s]));
  return map;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function SiteRenderer({
  site,
  slug,
}: {
  site: PublicSiteData;
  slug: string;
}) {
  const themeId = (site.website.themeId ?? "CLEAN_MODERN") as WebsiteThemeId;
  const layout = (site.website.layoutFamily ?? "CLASSIC")
    .toLowerCase()
    .replace(/_/g, "-");
  const vars = themeCssVars(
    themeId,
    site.business.primaryColor,
    site.business.secondaryColor,
  );
  const sections = sectionMap(site);
  const order = site.website.sectionOrder?.length
    ? site.website.sectionOrder
    : [
        "header",
        "hero",
        "about",
        "services",
        "pricing",
        "serviceAreas",
        "gallery",
        "testimonials",
        "faq",
        "bookingCta",
        "contact",
        "footer",
      ];

  const hero = sections.get("hero");
  const about = sections.get("about");
  const servicesSection = sections.get("services");
  const pricing = sections.get("pricing");
  const areas = sections.get("serviceAreas");
  const gallery = sections.get("gallery");
  const testimonials = sections.get("testimonials");
  const faq = sections.get("faq");
  const bookingCta = sections.get("bookingCta");
  const contact = sections.get("contact");
  const footer = sections.get("footer");
  const header = sections.get("header");

  const ctaLabel =
    asString(bookingCta?.content.cta) ||
    asString(hero?.content.cta) ||
    site.business.callToAction ||
    "Book a Service";

  const enabled = new Set(site.website.enabledSections ?? order);

  return (
    <div
      className={`public-site layout-${layout}`}
      style={vars as CSSProperties}
    >
      {!site.website.enabledSections?.length || enabled.has("header") ? (
        <header className="site-header">
          <Link className="logo" to={sitePath(slug)}>
            {site.business.name}
          </Link>
          {header?.content.showNav !== false && (
            <nav className="site-nav">
              <a href="#about">About</a>
              <a href="#services">Services</a>
              <a href="#contact">Contact</a>
              <Link to={sitePath(slug, "book")}>{ctaLabel}</Link>
              <Link to={sitePath(slug, "login")}>Client login</Link>
            </nav>
          )}
        </header>
      ) : null}

      {order.map((key) => {
        if (!enabled.has(key) && key !== "header") return null;

        if (key === "hero") {
          return (
            <section className="site-hero" key={key}>
              <div>
                <p className="eyebrow">
                  {site.business.industry.replace("_", " ")}
                </p>
                <h1>
                  {asString(hero?.content.headline) ||
                    site.business.heroHeadline ||
                    `Welcome to ${site.business.name}`}
                </h1>
                <p>
                  {asString(hero?.content.subheadline) ||
                    site.business.description ||
                    "Professional service you can trust."}
                </p>
                <div className="cta-row">
                  <Link className="site-cta-btn" to={sitePath(slug, "book")}>
                    {ctaLabel}
                  </Link>
                </div>
              </div>
            </section>
          );
        }

        if (key === "about") {
          return (
            <section className="site-section" id="about" key={key}>
              <h2>{about?.title || "About"}</h2>
              <p>
                {asString(about?.content.body) ||
                  site.business.aboutContent ||
                  `Learn more about ${site.business.name}.`}
              </p>
            </section>
          );
        }

        if (key === "services") {
          return (
            <section className="site-section" id="services" key={key}>
              <h2>{servicesSection?.title || "Services"}</h2>
              <p>
                {asString(servicesSection?.content.intro) || "Our services"}
              </p>
              <div className="service-list">
                {site.services.map((service) => (
                  <article className="service-item" key={service.id}>
                    <h3>{service.name}</h3>
                    {service.description ? <p>{service.description}</p> : null}
                    <strong>
                      {formatMoney(
                        service.priceCents,
                        site.business.currencyCode ?? "USD",
                      )}
                      {service.pricingType === "HOURLY" ? " / hr" : ""}
                      {service.pricingType === "STARTING_AT" ? "+" : ""}
                    </strong>
                  </article>
                ))}
                {site.services.length === 0 ? (
                  <p className="notice">Services coming soon.</p>
                ) : null}
              </div>
            </section>
          );
        }

        if (key === "pricing") {
          return (
            <section className="site-section" key={key}>
              <h2>{pricing?.title || "Pricing"}</h2>
              <p>
                {asString(pricing?.content.intro) ||
                  "Transparent pricing for every service."}
              </p>
              <div className="service-list">
                {site.services.map((service) => (
                  <div className="service-item" key={service.id}>
                    <strong>{service.name}</strong>
                    <div>
                      {service.pricingType === "QUOTE_REQUIRED"
                        ? "Custom quote"
                        : formatMoney(
                            service.priceCents,
                            site.business.currencyCode ?? "USD",
                          )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        }

        if (key === "serviceAreas") {
          return (
            <section className="site-section" key={key}>
              <h2>{areas?.title || "Service Areas"}</h2>
              <p>{asString(areas?.content.intro) || "Where we work"}</p>
              <div className="area-list">
                {(site.serviceAreas.length
                  ? site.serviceAreas.map((a) => a.name)
                  : asArray<string>(areas?.content.areas)
                ).map((name) => (
                  <div className="service-item" key={name}>
                    {name}
                  </div>
                ))}
              </div>
            </section>
          );
        }

        if (key === "gallery") {
          const images = asArray<{ url?: string; alt?: string } | string>(
            gallery?.content.images,
          );
          return (
            <section className="site-section" key={key}>
              <h2>{gallery?.title || "Gallery"}</h2>
              <div className="gallery-grid">
                {images.map((img, index) => {
                  const url = typeof img === "string" ? img : img.url;
                  if (!url) return null;
                  return (
                    <img
                      key={`${url}-${index}`}
                      src={url}
                      alt={
                        typeof img === "string"
                          ? "Gallery"
                          : img.alt || "Gallery"
                      }
                    />
                  );
                })}
                {images.length === 0 ? (
                  <p className="notice">Photos coming soon.</p>
                ) : null}
              </div>
            </section>
          );
        }

        if (key === "testimonials") {
          const items = site.reviews.length
            ? site.reviews.map((r) => ({
                quote: r.comment || `${r.rating}/5 stars`,
                name: r.client?.user?.fullName || "Client",
              }))
            : asArray<{ quote?: string; name?: string }>(
                testimonials?.content.items,
              );
          return (
            <section className="site-section" key={key}>
              <h2>{testimonials?.title || "Testimonials"}</h2>
              <div className="testimonial-list">
                {items.map((item, index) => (
                  <blockquote className="testimonial-item" key={index}>
                    <p>“{item.quote || "Great service."}”</p>
                    <footer>— {item.name || "Client"}</footer>
                  </blockquote>
                ))}
              </div>
            </section>
          );
        }

        if (key === "faq") {
          const items = asArray<{ question?: string; answer?: string }>(
            faq?.content.items,
          );
          return (
            <section className="site-section" key={key}>
              <h2>{faq?.title || "FAQ"}</h2>
              <div className="faq-list">
                {items.map((item, index) => (
                  <details className="faq-item" key={index}>
                    <summary>{item.question || "Question"}</summary>
                    <p>{item.answer || ""}</p>
                  </details>
                ))}
                {items.length === 0 ? (
                  <p className="notice">FAQs will appear here.</p>
                ) : null}
              </div>
            </section>
          );
        }

        if (key === "bookingCta") {
          return (
            <section className="site-section" key={key}>
              <h2>
                {asString(bookingCta?.content.headline) || "Ready to book?"}
              </h2>
              <p>Schedule a service with {site.business.name}.</p>
              <Link className="site-cta-btn" to={sitePath(slug, "book")}>
                {ctaLabel}
              </Link>
            </section>
          );
        }

        if (key === "contact") {
          return (
            <section className="site-section" id="contact" key={key}>
              <h2>{contact?.title || "Contact"}</h2>
              <p>
                {site.business.phoneNumber ||
                  asString(contact?.content.phone) ||
                  "Phone coming soon"}
              </p>
              <p>
                {site.business.email || asString(contact?.content.email) || ""}
              </p>
              {(site.business.city || site.business.region) && (
                <p>
                  {[
                    site.business.city,
                    site.business.region,
                    site.business.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
            </section>
          );
        }

        if (key === "footer") {
          return (
            <footer className="site-footer" key={key}>
              <p>
                © {new Date().getFullYear()} {site.business.name}
              </p>
              {footer?.content.showPolicies !== false &&
              site.policies.length > 0 ? (
                <p>{site.policies.map((p) => p.title).join(" · ")}</p>
              ) : null}
            </footer>
          );
        }

        return null;
      })}
    </div>
  );
}
